import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useContext } from './context';
import { Adjustments } from './adjustments/Adjustments';
import { LimeAdjustments } from './adjustments/LimeAdjustments';
import { PhosphorusAdjustments } from './adjustments/PhosphorusAdjustments';
import { PotassiumAdjustments } from './adjustments/PotassiumAdjustments';
import * as api from '../../lib/api.js';
import { Table } from '../../components/table/Table';
import { useAlerts } from '../../hooks/use-alerts';
import { usePermission } from '../../hooks/use-permission.js';
import { YearSelector } from './YearSelector.jsx';
import { useColumns } from './use-columns.jsx';
import { useActions } from './actions/use-actions.jsx';
import { TogglePanelButton } from '../../components/side-panel/TogglePanelButton';
import { FieldSidePanel } from './side-panel/FieldSidePanel.jsx';
import { useFields } from '../../hooks/use-fields.js';

const TYPE_CONFIG = {
  L: {
    name: 'Lime',
    adjustmentComponent: LimeAdjustments,
    saveGroupAdjustments: api.setLpkLimeGroupAdjustment,
    saveFieldAdjustment: api.setLpkLimeAdjustmentFactor,
  },
  P: {
    name: 'Phosphorus',
    adjustmentComponent: PhosphorusAdjustments,
    saveGroupAdjustments: api.setLpkPhosphorusGroupAdjustment,
    saveFieldAdjustment: api.setLpkPhosphorusAdjustmentFactor,
  },
  K: {
    name: 'Potassium',
    adjustmentComponent: PotassiumAdjustments,
    saveGroupAdjustments: api.setLpkPotassiumGroupAdjustment,
    saveFieldAdjustment: api.setLpkPotassiumAdjustmentFactor,
  },
};

export function FieldsTab({ tabs, type, renderSidePanel }) {
  const {
    isLoading,
    tab,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    getFields,
    isSidePanelVisible,
    toggleSidePanel,
    year,
  } = useContext();
  const { showError } = useAlerts();
  const canAdjust = usePermission('RUN_RX');
  const [adjustField, setAdjustField] = useState();
  const config = useMemo(() => TYPE_CONFIG[type], [type]);
  const { buildColumns } = useColumns();
  const { actions } = useActions();
  const { focusedAaid } = useFields();

  const fields = useMemo(() => getFields(type), [getFields, type]);

  const focusedField = useMemo(
    () => fields.find((f) => f.aaid === focusedAaid),
    [fields, focusedAaid],
  );

  const saveAdjustments = useCallback(
    (field, fieldAdjustment, groups) =>
      Promise.all([
        config.saveFieldAdjustment(field, fieldAdjustment),
        config.saveGroupAdjustments(field, groups),
      ]).catch(showError),
    [config, showError],
  );

  useEffect(() => {
    if (tab === type) {
      renderSidePanel(
        isSidePanelVisible ? (
          <FieldSidePanel
            close={toggleSidePanel}
            year={year}
            field={focusedField}
          />
        ) : null,
      );
    }
  }, [
    toggleSidePanel,
    isSidePanelVisible,
    focusedField,
    year,
    renderSidePanel,
    tab,
    type,
  ]);

  return tab === type ? (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {buildAdjustments()}
      {buildTable()}
    </Box>
  ) : null;

  function buildAdjustments() {
    if (!adjustField) return null;

    return (
      <Adjustments
        title={`${adjustField.fieldName} - ${config.name}`}
        field={adjustField}
        Component={config.adjustmentComponent}
        handleClose={() => setAdjustField(null)}
        handleSave={saveAdjustments}
        canAdjust={canAdjust}
      />
    );
  }

  function buildTable() {
    return (
      <Table
        hiddenColumns={['fieldId']}
        checkboxSelection
        disableRowSelectionOnClick
        columns={buildColumns(type, setAdjustField)}
        fields={fields}
        loading={isLoading}
        tabs={tabs}
        actions={actions}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        preActions={buildPreActions()}
      />
    );
  }

  function buildPreActions() {
    return [
      <YearSelector key="year" />,
      <TogglePanelButton
        key="side-panel"
        active={isSidePanelVisible}
        onClick={toggleSidePanel}
      />,
    ].filter(Boolean);
  }
}
